import { NotifyFormat } from './constants';
import {
  DealerId,
  DisabledNotificationChannelType,
  DisabledNotificationType,
} from './dealerTypes';

/* For a given channel (e.g. email) and template ID (e.g. testDriveEmail),
 * a template customization, also known as "Outreach Form", represents the
 * customized field values that a dealer has configured for the template (e.g.
 * field "Email Subject Line" with value "Your vehicle is ready for pickup!",
 * along with 10 or so other fields, customized for the confirmation email
 * template). */
export type OutreachFormId = string;

// This interface represents the fields of the Sequelize data schema.
export interface OutreachForm {
  id?: OutreachFormId;
  dealerId: DealerId;
  templateId: NotifyFormat;
  values?: Partial<Record<FieldId, string>>;
  createdAt?: Date;
  updatedAt?: Date;
}

interface OutreachFormStatus {
  enabled?: boolean;
}

/* Backend API requests and responses for OutreachForm also include a synthetic
 * property "enabled" that is retrieved from and stored in the Dealer schema.
 * The following interface supports accepting and returning that type of data
 * from API endpoints.  This property is separated from the OutreachForm
 * interface so that OutreachForm can have parity with its Sequelize schema. */
export interface OutreachFormWithStatus
  extends OutreachForm,
    OutreachFormStatus {}

/* This interface represents a simplified description of an OutreachForm and
 * whether it is enabled, used by Chairman to render the list of OutreachForms
 * and their status.  This is necessary because 'enabled' is a synthetic
 * property, calculated from nested properties on the Dealer object, and thus
 * does not reside within the OutreachForm object.  For scenarios in which a
 * Dealer has not previously configured an OutreachForm template, only the
 * Dealer object properties will exist, and the enabled status needs a way to
 * be represented even if their corresponding OutreachForm objects have not yet
 * been created.  Thus, this interface lacks OutreachForm id properties etc. */
export type OutreachFormStatusList = Partial<
  Record<NotifyFormat, OutreachFormStatus>
>;

/* The template types in NotifyFormat are not identical to the template types
 * that appear within "Dealer.disabledNotifications".  The following interface
 * allows templates, whose TemplateID is NotifyFormat, to be mapped to
 * Dealer.disabledNotifications. */
export interface DealerDisabledProperty {
  channel: DisabledNotificationChannelType;
  property: DisabledNotificationType;
}

/* For a given channel (e.g. email) and template ID (e.g. testDriveEmail),
 * a template definition represents the associated fields and default values
 * for the template.  Chairman uses template definitions to draw the UI that
 * allows users to customize templates.  Backend uses template definitions to
 * augment template customization data with default field values. */
export interface TemplateDefinition {
  name: string;
  dealerDisabledProperty?: DealerDisabledProperty;
  fieldIds: FieldIdType[];
}

export type TemplateDefinitions = Partial<
  Record<NotifyFormat, TemplateDefinition>
>;

// Maps templates to the default values for their constituent fields.
export type TemplateDefaultsType = Partial<Record<NotifyFormat, FieldValues>>;

/* Fields represent the text values that can be customized by dealers within
 * the event-based digital communications sent by backend, e.g. confirmation
 * emails sent to customers when vehicles have been ordered.  Fields are used
 * within template definitions to define an ordered list of fields that will
 * be presented to users in Chairman.  Fields are also used by template
 * customizations to store dealer-specified text values for each field.  A
 * separate data structure defines the default values for each field. */

// List of valid field IDs, mapped to human-friendly name.
export enum FieldId {
  body = 'body',
  column1Body = 'column1Body',
  column1Title = 'column1Title',
  column2Body = 'column2Body',
  column2Title = 'column2Title',
  column3Body = 'column3Body',
  column3Title = 'column3Title',
  dealLinkText = 'dealLinkText',
  emailSubject = 'emailSubject',
  greeting = 'greeting',
  locationAddress = 'locationAddress',
  pickupWindow = 'pickupWindow',
  title = 'title',
}

export type FieldIdType = keyof typeof FieldId;
export type FieldValues = Partial<Record<FieldIdType, string>>;

export type FieldDefaultsType = {
  [templateId in NotifyFormat]?: FieldValues;
};

/* Tags are text identifiers that, when present within template customization
 * fields, are expanded into dynamic values based on logic within backend.
 * For example, for a template customization field that contains the text
 * 'Thank you for visiting [Dealer Name]!', the text '[Dealer Name]' would be
 * replaced with the corresponding dealership's name.  The enum identifiers are
 * the canonical version of the tag name, and the human-readable versions are
 * defined within constants/outreach; tag-processing logic should convert from
 * human-readable to canonical, when appropriate. */

// List of valid tag IDs.
export enum TagId {
  CAR = 'CAR',
  DATE = 'DATE',
  DATE_AND_TIME = 'DATE_AND_TIME',
  DEALER_ADDRESS = 'DEALER_ADDRESS',
  DEALER_NAME = 'DEALER_NAME',
  METHOD = 'METHOD',
  TIME = 'TIME',
}

type TagIdsType = {
  [tagId in keyof typeof TagId]?: string;
};

export type TagsType =
  | {
      default?: TagIdsType;
    }
  | {
      [templateId in NotifyFormat]: TagIdsType;
    };

export enum PreviewFormat {
  HTML = 'html',
  JPG = 'jpg',
  PDF = 'pdf',
}

export type PreviewContent = {
  format: PreviewFormat;
  subject?: string;
  html?: string;
  url?: string;
};
